/** Returns the set of all `nr` values that have at least one child (used for Expand All) */
export const collectAllExpandable = (chartData) => {
  const nrs = new Set();
  const traverse = (node) => {
    if (!node.children?.length) return;
    nrs.add(node.nr);
    node.children.forEach(traverse);
  };
  (Array.isArray(chartData) ? chartData : [chartData]).forEach(traverse);
  return nrs;
}

/**
 * Returns `nr` values for nodes that must be expanded to show `maxDepth` levels.
 * maxDepth 1 = first level expanded, 2 = two levels open, etc.
 */
export const collectExpandedToDepth = (chartData, maxDepth) => {
  const nrs = new Set();
  const traverse = (node, depth) => {
    if (depth >= maxDepth || !node.children?.length) return;
    nrs.add(node.nr);
    node.children.forEach((child) => traverse(child, depth + 1));
  };
  (Array.isArray(chartData) ? chartData : [chartData]).forEach((root) => traverse(root, 0));
  return nrs;
}

/** Derives dim and team-color state for a node given the current search/filter context */
export const resolveNodeDisplay = (nr, query, searchMatches, activeFilter, teams) => {
  const searchActive = query.trim().length > 0;
  const isSearchMatch = searchActive && searchMatches.has(nr);
  const activeTeam = activeFilter !== null ? teams.find(team => team.teamCode === activeFilter) : null;
  const isInActiveTeam = activeTeam ? activeTeam.memberNrs.has(nr) : false;
  const isDimmed = searchActive ? !isSearchMatch : (activeFilter !== null && !isInActiveTeam);
  const nodeTeamColor = (activeFilter !== null && isInActiveTeam) ? activeTeam.color : null;
  return { isDimmed, nodeTeamColor };
}

/** Builds the tooltip string for a node card: name + job title + expand/collapse hint + ended indicator */
export const buildNodeTooltip = (name, title, hasChildren, isExpanded, t, employmentEnded = false) => {
  const endedHint = employmentEnded ? t('EmploymentEnded') : null;
  const clickHint = hasChildren
    ? (isExpanded ? t('ClickToCollapse') : t('ClickToExpand'))
    : null;
  return [name, title, endedHint, clickHint].filter(Boolean).join(' - ');
}

/** Prunes ended-employment leaf nodes from the tree; marks ended nodes with active descendants (i.e. subordinates) as `employmentEnded: true` */
export const preprocessChart = (node) => {
  const today = new Date().toISOString().slice(0, 10);

  const process = (person) => {
    const processedChildren = (person.children || [])
      .map(process)
      .filter(Boolean);

    const hasEnded = person.endDate != null && person.endDate <= today;

    if (hasEnded && processedChildren.length === 0) {
      return null; // prune: ended with no active subordinates
    }

    return { ...person, children: processedChildren, employmentEnded: hasEnded };
  };

  return process(node);
}
